import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { PAYMENT_METHODS, money, todayISO } from "@/lib/format";

const Line = ({ label, v, cls = "text-slate-200", testId }) => (
  <div className="flex justify-between text-sm"><span className="text-slate-400">{label}</span><span className={`mono ${cls}`} data-testid={testId}>{v}</span></div>
);

// Registra un abono (parcial o total) a una factura pendiente
export const PayDialog = ({ invoice, onOpenChange, onDone }) => {
  const [method, setMethod] = useState("Efectivo");
  const [f, setF] = useState({ amount: 0, date: todayISO(), note: "" });
  useEffect(() => {
    if (!invoice) return;
    setMethod(invoice.payment_method === "Crédito" ? "Efectivo" : invoice.payment_method || "Efectivo");
    setF({ amount: invoice.balance, date: todayISO(), note: "" });
  }, [invoice]);

  const pay = async () => {
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/payments`, { amount: Number(f.amount) || 0, method, date: f.date, note: f.note });
      toast.success(data.status === "pagada" ? `Factura ${invoice.number} pagada por completo` : `Abono registrado · saldo ${money(data.balance, data.currency)}`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#120E2E] border-[#271E56] max-w-sm" data-testid="pay-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-display">Registrar abono · <span className="mono text-[#00F0FF]">{invoice?.number}</span></DialogTitle>
        </DialogHeader>
        {invoice && (
          <div className="space-y-1.5 rounded-xl bg-[#0C0822] border border-[#271E56] p-3">
            <Line label="Total factura" v={money(invoice.total, invoice.currency)} />
            <Line label="Abonado" v={money(invoice.paid_amount, invoice.currency)} cls="text-emerald-300" />
            <Line label="Saldo pendiente" v={money(invoice.balance, invoice.currency)} cls="text-amber-300 font-bold" testId="pay-current-balance" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="label-cap">Monto</Label><Input type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="pay-amount-input" /></div>
          <div className="space-y-1.5"><Label className="label-cap">Fecha</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="pay-date-input" /></div>
        </div>
        {invoice && Number(f.amount) < invoice.balance && Number(f.amount) > 0 && (
          <div className="text-xs text-slate-400" data-testid="pay-new-balance">Saldo después del abono: <span className="mono text-amber-300">{money(invoice.balance - Number(f.amount), invoice.currency)}</span></div>
        )}
        <Input placeholder="Nota (n.º comprobante, referencia…)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="pay-note-input" />
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PAYMENT_METHODS.filter((m) => m !== "Crédito").map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button variant="outline" onClick={() => setF({ ...f, amount: invoice.balance })} data-testid="pay-full-btn">Saldo total</Button>
          <Button className="btn-neon" onClick={pay} data-testid="pay-confirm-btn">Registrar abono</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
